/**
 * The campaign modifiers that change the rules on the ground (04 §7) and had no effect until now:
 *
 *   scattered-drop   strong variable wind: a falling pod is pushed by three times the wind, and by a gust of its
 *                    own - so pods let go together come down far apart, and more of them on bad ground;
 *   hot-extraction   the pickup is contested: when the Osprey turns inbound the garrison goes to full alarm, the
 *                    nearest squad makes for the X, and the aircraft holds over it for less long.
 *
 * and the one intel card that decides where the aircraft goes:
 *
 *   alternate-lz     two extraction points instead of one; the Osprey settles on one as it turns inbound.
 *
 * (Radio Silence is presentation only: the HUD reads `MissionDef.modifiers` itself.) Everything else a modifier
 * does is baked into the `MissionDef` by the generator. Pure and deterministic: gusts come from the mission seed.
 */

import { raiseAlert, sendToInvestigate } from './alert';
import { type Ctx, isFighter } from './ctx';
import { isAutomated } from './enemies/specials/kinds';
import { cos, sin, TAU } from './math';
import { Pcg32 } from './rng';
import { INTEG3, OSPREY } from './tuning';
import type { MissionDef } from './types';

/** Metres per second a falling pod is pushed: the mission's wind - under Scattered Drop, far more, and its own gust. */
export function podWind(def: MissionDef, index: number): { x: number; z: number } {
  if (!def.modifiers.includes('scattered-drop')) return def.wind;
  const rng = new Pcg32(def.seed, 40 + index);
  const bearing = rng.float() * TAU;
  const gust = INTEG3.podGust * (0.5 + 0.5 * rng.float());
  return {
    x: def.wind.x * INTEG3.scatteredDrift + sin(bearing) * gust,
    z: def.wind.z * INTEG3.scatteredDrift + cos(bearing) * gust,
  };
}

/** How long the Osprey holds over the X. */
export function hoverTicks(def: MissionDef): number {
  const hot = def.modifiers.includes('hot-extraction');
  return Math.round(OSPREY.hoverTicks * (hot ? INTEG3.hotHoverFactor : 1));
}

/**
 * Alternate LZ (04 §8): there are two X's, and the Osprey comes to ONE of them.
 *
 * The choice is settled the moment it turns inbound - by Recall, by the clock running out, or by the
 * mission itself - and is never revisited: an aircraft that changed its mind halfway would have him
 * running between two points he cannot see, which is worse than having only one. It comes to whichever
 * is NEARER when it is called, because that is the only rule he can work out for himself while he is
 * running, and running is when he needs to know.
 *
 * A mission that MOVES its X keeps its one point: mission 4 takes the Osprey to wherever the officer was
 * taken (`objectives/officer.ts`), and the choice is only offered while the X is still where the mission
 * put it. Runs before `contestPickup`, which sends the garrison to the X that was chosen.
 */
export function chooseLandingZone(ctx: Ctx): void {
  const def = ctx.statics.def;
  const alt = def.pickupAlt;
  if (alt === undefined) return;
  const o = ctx.world.objective;
  if (o.pickupX !== def.pickup.x || o.pickupZ !== def.pickup.z) return;
  const r = ctx.world.ranger;
  const away = (x: number, z: number): number => (r.x - x) * (r.x - x) + (r.z - z) * (r.z - z);
  if (away(alt.x, alt.z) >= away(def.pickup.x, def.pickup.z)) return;
  o.pickupX = alt.x;
  o.pickupZ = alt.z;
}

/** Hot Extraction: the Osprey has just turned inbound - full alarm, and the nearest squad converges on the pickup. */
export function contestPickup(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  if (!statics.def.modifiers.includes('hot-extraction')) return;
  const x = w.objective.pickupX;
  const z = w.objective.pickupZ;
  raiseAlert(ctx, 3, x, z);
  const away = (e: { x: number; z: number }): number => (e.x - x) * (e.x - x) + (e.z - z) * (e.z - z);
  const squad = w.enemies
    .filter((e) => isFighter(e) && !isAutomated(e.kind) && e.mans === null && e.state !== 'down')
    .sort((a, b) => away(a) - away(b) || a.id - b.id)
    .slice(0, INTEG3.hotSquad);
  for (const e of squad) {
    e.wary = true;
    sendToInvestigate(e, x, z, true);
  }
}
